"use client"

import { useSetAtom } from "jotai"
import Link from "next/link"
import posthog from "posthog-js"
import { useEffect } from "react"
import { Prose } from "@/components/content/prose"
import { SupportDialog } from "@/components/support-dialog"
import { showSupportDialogAtom } from "@/components/support-dialog/atoms"
import { Button, buttonVariants } from "@/components/ui/button"
import RootLayout from "./root-layout"

interface ErrorPageProps {
  error: Error
  reset: () => void
}

export default function ErrorPage(props: ErrorPageProps) {
  useEffect(() => {
    posthog.captureException(props.error)
  }, [props.error])

  return (
    <RootLayout locale="en">
      <ErrorPageBody {...props} />

      <SupportDialog dialogContentProps={{ className: "dark" }} />
    </RootLayout>
  )
}

function ErrorPageBody({ error, reset }: ErrorPageProps) {
  const setShowSupportDialog = useSetAtom(showSupportDialogAtom)

  return (
    <div className="dark flex h-full grow flex-col">
      <div className="flex grow flex-col items-center justify-center gap-12 bg-background p-4 text-foreground">
        <span className="select-none bg-linear-to-b from-foreground/20 to-foreground/5 bg-clip-text font-black text-9xl text-transparent blur-xs dark:from-foreground/15 dark:to-foreground/0">
          500
        </span>

        <main className="flex flex-col items-center gap-2">
          <h1 className="font-bold text-3xl tracking-tight">Something went wrong!</h1>
          <p className="max-w-100 text-center text-muted-foreground">
            We've logged this error and will look into it.
          </p>
        </main>

        <nav className="flex items-center gap-4">
          <Link
            className={buttonVariants({
              variant: "default",
              className: "text-primary-foreground!",
            })}
            href="/app"
          >
            Back to App
          </Link>
          <Button variant="outline" onClick={() => reset()}>
            Try again
          </Button>
        </nav>

        <Prose size="sm" className="opacity-30">
          Please{" "}
          <Button
            variant="link"
            className="h-auto p-0 underline decoration-muted-foreground/50 hover:decoration-muted-foreground/70"
            onClick={() => setShowSupportDialog(true)}
          >
            contact us
          </Button>{" "}
          if the problem persists.
        </Prose>
      </div>
    </div>
  )
}
